import { useEffect, useState, type ComponentType } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { refreshSession, useSession } from "../net/session.js";
import { warmTabs } from "../net/prefetch.js";
import { Login } from "../screens/Login.js";
import { RoomWait } from "../screens/RoomWait.js";
import { ReplayViewer } from "../screens/ReplayViewer.js";
import { Table } from "../screens/Table.js";
import { Spinner } from "../ui/primitives.js";
import { AppShell, FlowShell, PublicReplayShell } from "./AppShell.js";
import { WideAppShell, WideFlowShell, WidePublicReplayShell } from "./WideShell.js";
import { ActivityProvider } from "./ActivityController.js";
import { Home, MeScreen, PartyScreen, RecordScreen, RulesScreen, SocialScreen } from "../screens/MobileScreens.js";
import { ResultScreen } from "../screens/Result.js";
import { LiveWatchScreen, NotificationsScreen, PlayerScreen } from "../screens/UtilityScreens.js";
import { WideHome, WideMeScreen, WideRecordScreen, WideSocialScreen } from "../screens/wide/WideScreens.js";
import { LocaleProvider } from "../i18n.js";
import { destinationForMode } from "./navigation.js";
import { isWidePath, pathForMode, toMobilePath } from "./widePath.js";
import { useViewportLayoutMode } from "./useViewportLayoutMode.js";
import { UiPreferencesProvider } from "./UiPreferences.js";
import "./tokens.css";
import "./experience.css";

interface ShellSet {
  App: ComponentType;
  Flow: ComponentType;
  PublicReplay: ComponentType;
  wide: boolean;
}

const MOBILE_SHELLS: ShellSet = { App: AppShell, Flow: FlowShell, PublicReplay: PublicReplayShell, wide: false };
const WIDE_SHELLS: ShellSet = { App: WideAppShell, Flow: WideFlowShell, PublicReplay: WidePublicReplayShell, wide: true };

export function App() {
  useEffect(() => {
    void (async () => {
      const params = new URLSearchParams(window.location.search);
      const debug = import.meta.env.DEV && params.get("debug") === "1";
      if (debug) {
        const fixture = params.get("debugUser");
        await fetch(`/api/dev/session${fixture ? `?userId=${encodeURIComponent(fixture)}` : ""}`);
      }
      const session = await refreshSession();
      if (session.status === "signedIn") warmTabs();
    })();
  }, []);
  return (
    <BrowserRouter>
      <LocaleProvider>
        <UiPreferencesProvider>
          <LayoutRedirector />
          <RouteAnnouncer />
          <Routes>
            <Route element={<PublicReplayShell />}><Route path="/r/:slug" element={<ReplayViewer shared />} /></Route>
            <Route element={<WidePublicReplayShell />}><Route path="/w/r/:slug" element={<ReplayViewer shared />} /></Route>
            <Route path="*" element={<AdaptiveGate />} />
          </Routes>
        </UiPreferencesProvider>
      </LocaleProvider>
    </BrowserRouter>
  );
}

function LayoutRedirector() {
  const location = useLocation();
  const navigate = useNavigate();
  const mode = useViewportLayoutMode();
  useEffect(() => {
    if ((mode === "wide") === isWidePath(location.pathname)) return;
    navigate({ pathname: pathForMode(location.pathname, mode), search: location.search, hash: location.hash }, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate, mode]);
  return null;
}

function RouteAnnouncer() {
  const location = useLocation();
  const [title, setTitle] = useState("Guandan");
  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      const heading = document.querySelector<HTMLElement>("main h1");
      const next = heading?.textContent?.trim() || "Guandan";
      setTitle(next); document.title = `${next} · Guandan`;
      heading?.setAttribute("tabindex", "-1"); heading?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(id);
  }, [location.pathname, location.search]);
  return <span className="sr-only" aria-live="polite">{title}</span>;
}

function AdaptiveGate() {
  const viewportMode = useViewportLayoutMode();
  return <Gate shells={viewportMode === "wide" ? WIDE_SHELLS : MOBILE_SHELLS} />;
}

function Gate({ shells }: { shells: ShellSet }) {
  const session = useSession();
  const location = useLocation();
  const prefix = shells.wide ? "/w" : "";
  if (session.status === "loading") return <div className={`app-frame boot${shells.wide ? " app-frame-wide" : ""}`}><Spinner label="Loading…" /></div>;
  if (session.status === "anonymous") return <Login wide={shells.wide} />;
  const routeLocation = { ...location, pathname: toMobilePath(location.pathname) };
  return <ActivityProvider>{renderPrivateRoutes(shells, prefix, routeLocation)}</ActivityProvider>;
}

function LegacyRedirect({ to, replay = false }: { to: string; replay?: boolean }) {
  const location = useLocation();
  const { roomId } = useParams();
  const mode = useViewportLayoutMode();
  return <Navigate replace to={destinationForMode({ pathname: replay && roomId ? `/record/replay/${roomId}` : to, search: location.search, hash: location.hash }, mode)} />;
}

function renderPrivateRoutes(shells: ShellSet, prefix: string, routeLocation: ReturnType<typeof useLocation>) {
  const AppShellComponent = shells.App;
  const FlowShellComponent = shells.Flow;
  const path = (suffix = "") => suffix || "/";
  return (
    <Routes location={routeLocation}>
      <Route path={path("/table/:roomId")} element={<Table wide={shells.wide} />} />
      <Route path={path("/watch/:roomId")} element={<Table wide={shells.wide} />} />
      <Route element={<FlowShellComponent />}>
        <Route path={path("/result/:roomId")} element={<ResultScreen wide={shells.wide} />} />
        <Route path={path("/record/match/:roomId")} element={<ResultScreen wide={shells.wide} />} />
        <Route path={path("/rules")} element={<RulesScreen />} />
        <Route path={path("/watch")} element={<LiveWatchScreen />} />
        <Route path={path("/player/:userId")} element={<PlayerScreen />} />
        <Route path={path("/party/:partyId")} element={<PartyScreen />} />
        <Route path={path("/notifications")} element={<NotificationsScreen />} />
        <Route path={path("/room/:code")} element={<RoomWait wide={shells.wide} />} />
      </Route>
      <Route element={<AppShellComponent />}>
        <Route path={path()} element={shells.wide ? <WideHome /> : <Home />} />
        <Route path={path("/record")} element={shells.wide ? <WideRecordScreen /> : <RecordScreen />} />
        <Route path={path("/record/replay/:roomId")} element={<ReplayViewer wide={shells.wide} />} />
        <Route path={path("/social")} element={shells.wide ? <WideSocialScreen /> : <SocialScreen />} />
        <Route path={path("/me")} element={shells.wide ? <WideMeScreen /> : <MeScreen />} />
        <Route path={path("/replay")} element={<LegacyRedirect to="/record" />} />
        <Route path={path("/replay/:roomId")} element={<LegacyRedirect to="/record" replay />} />
        <Route path={path("/ranking")} element={<LegacyRedirect to="/record" />} />
        <Route path={path("/friends")} element={<LegacyRedirect to="/social" />} />
        <Route path={path("/profile")} element={<LegacyRedirect to="/me" />} />
        <Route path="*" element={<Navigate to={prefix || "/"} replace />} />
      </Route>
    </Routes>
  );
}
